import { SharedAssetRequestStatus } from 'types/api/sharedAssets/requests'

import type { Team } from 'types/api/teams'

export type TeamNamespace = {
  status: string
  name: string
  teamId: string
  team: Team
  assetId: string
  clusterName: string
  owningTeamId: string
}

export const NAMESPACE_STATUS = {
  requested: SharedAssetRequestStatus.REQUESTED,
  approved: SharedAssetRequestStatus.APPROVED,
  denied: SharedAssetRequestStatus.DENIED,
  revoked: SharedAssetRequestStatus.REVOKED,
  deleted: SharedAssetRequestStatus.DELETED,
  shareable: 'SHAREABLE' // means it belongs to the cluster but the current team is able to request access
}

export const isRequested = (namespace) =>
  namespace.status === NAMESPACE_STATUS.requested
export const isDenied = (namespace) =>
  namespace.status === NAMESPACE_STATUS.denied
export const isApproved = (namespace) =>
  namespace.status === NAMESPACE_STATUS.approved
export const isRevoked = (namespace) =>
  namespace.status === NAMESPACE_STATUS.revoked
export const isDeleted = (namespace) =>
  namespace.status === NAMESPACE_STATUS.deleted
export const isShareable = (namespace) =>
  namespace.status === NAMESPACE_STATUS.shareable

export function getPendingRequests({
  namespaces = []
}: {
  namespaces: TeamNamespace[]
}) {
  return namespaces.filter((namespace) => isRequested(namespace))
}

export function getNamespaceDropdownGroup(namespace) {
  if (namespace.status === NAMESPACE_STATUS.requested) {
    return 'Pending Requests'
  } else if (
    namespace.status === NAMESPACE_STATUS.approved ||
    !namespace.status
  ) {
    return 'Available'
  } else {
    return 'Shareable Namespaces'
  }
}

export function getRequestId({ namespace, assetId, teamId }) {
  return `${teamId}#${assetId}#${namespace}`
}

export function createRequests({
  owningTeamId,
  requestingTeamId,
  assetId,
  namespaces
}) {
  if (!namespaces || namespaces.length === 0) return []
  return namespaces.map((namespace) => ({
    owningTeamId,
    teamToShareWith: requestingTeamId,
    assetId,
    namespace
  }))
}

export function getRequestNode({ rawRequest, team, isShareable }) {
  return {
    status: isShareable ? 'SHAREABLE' : rawRequest.currentStatus,
    name: rawRequest.namespace,
    requestId: rawRequest.requestId,
    teamId: rawRequest?.sharedTeam,
    assetId: rawRequest.assetId,
    clusterName: rawRequest.assetName,
    owningTeamId: rawRequest.owningTeamId,
    team
  }
}

export function getClusterByKey({ clusters, key, value }) {
  if (!key) return {}
  return clusters.filter((cluster) => cluster[key] === value)?.[0] ?? {}
}

// BE :  Here if you own the cluster the BE sends the available clusters into
// the availableNamespaces array
// and in currentlySharedNamespaces if you don't
export function getSharedNamespacesFromCluster(clusterObject, currentTeamId) {
  return (
    clusterObject?.[
      currentTeamId === clusterObject?.owningTeamId
        ? 'availableNamespaces'
        : 'currentlySharedNamespaces'
    ] ?? []
  )
}

//////// End of BE

export function getAvailableNamespacesFromCluster(
  clusterObject,
  currentTeamId
) {
  return currentTeamId === clusterObject?.owningTeamId
    ? []
    : clusterObject?.availableNamespaces ?? []
}
